import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Users, Zap, Target } from 'lucide-react';

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: Code,
      title: 'Clean Code',
      description: 'Writing maintainable and scalable code with best practices and design patterns.',
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Experienced in working with cross-functional teams and agile methodologies.',
    },
    {
      icon: Zap,
      title: 'Performance Focus',
      description: 'Optimizing applications for speed, efficiency, and excellent user experience.',
    },
    {
      icon: Target,
      title: 'Problem Solving',
      description: 'Analytical approach to complex technical challenges and innovative solutions.',
    },
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About <span className="gradient-text">Me</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-purple-600 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-900">
              Passionate Software Developer
            </h3>
            
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                I'm a dedicated software developer with a passion for creating innovative 
                web applications that solve real-world problems. With expertise in modern 
                technologies like React, Node.js, and TypeScript, I bring ideas to life 
                through clean, efficient, and scalable code.
              </p>
              
              <p>
                My journey in software development started with a curiosity about how 
                things work on the web, which evolved into a deep love for building 
                user-friendly applications. I believe in writing code that not only 
                works but is also maintainable, readable, and follows industry best practices.
              </p>
              
              <p>
                When I'm not coding, you can find me exploring new technologies, 
                I'm always eager to learn and adapt to new 
                challenges in this ever-evolving field.
              </p>
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{feature.title}</h4>
                    <p className="text-gray-600 text-xs mt-1">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Stats/Info Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="bg-gradient-to-br from-primary-500 to-primary-600 text-white p-6 rounded-xl text-center card-hover"
              >
                <div className="text-3xl font-bold mb-2">3+</div>
                <div className="text-sm opacity-90">Years Experience</div>
              </motion.div>
              
              {/* <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl text-center card-hover"
              >
                <div className="text-3xl font-bold mb-2">20+</div>
                <div className="text-sm opacity-90">Projects Completed</div>
              </motion.div> */}
              
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl text-center card-hover"
              >
                <div className="text-3xl font-bold mb-2">15+</div>
                <div className="text-sm opacity-90">Technologies</div>
              </motion.div>
              
              {/* <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.9 }}
                className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-xl text-center card-hover"
              >
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-sm opacity-90">Client Satisfaction</div>
              </motion.div> */}
            </div>

            {/* Additional Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 1 }}
              className="bg-gray-50 p-6 rounded-xl border border-gray-200"
            >
              <h4 className="font-semibold text-gray-900 mb-3">What I'm Looking For</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                  Challenging full-stack development opportunities
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                  Remote or hybrid work environments
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                  Collaborative team environments
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                  Continuous learning and growth opportunities
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 